import React, { useState } from 'react';
import axios from 'axios';
import { FileText, Loader2 } from 'lucide-react';
import { PACKAGES } from './PackagePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { age: 'Age', sex: 'Sex', symptoms: 'Symptoms', diagnosis: 'Diagnosis', medications: 'Medicines', allergies: 'Allergies', other_conditions: 'Other conditions' };

const ReportButton = ({ r }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/reports/${r.id}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      alert('Could not open this report.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" data-testid={`booking-report-${r.id}`} onClick={open}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-teal-100 text-xs font-semibold text-teal-800 hover:border-teal-400 max-w-[220px]">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} <span className="truncate">{r.filename}</span>
    </button>
  );
};

export const ReportList = ({ reports }) => reports?.length ? (
  <div className="flex flex-wrap gap-2">{reports.map((r) => <ReportButton key={r.id} r={r} />)}</div>
) : null;

export const BookingActions = ({ b, reload }) => {
  const [busy, setBusy] = useState('');
  if (b.status !== 'awaiting_transfer') return null;
  const act = async (kind) => {
    if (kind === 'cancel' && !window.confirm('Cancel this booking and free the slot?')) return;
    setBusy(kind);
    try { await axios.post(`${API}/intl-bookings/${b.id}/${kind}`, {}, { withCredentials: true }); await reload(); } catch { alert('Could not update this booking.'); } finally { setBusy(''); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      {b.reference && <span data-testid={`booking-reference-${b.id}`} className="text-xs text-slate-500">Wise reference <strong className="text-slate-700">{b.reference}</strong></span>}
      <button type="button" data-testid={`booking-mark-paid-${b.id}`} disabled={!!busy} onClick={() => act('mark-paid')} className="btn-teal px-4 py-1.5 rounded-full text-xs font-semibold disabled:opacity-60">{busy === 'mark-paid' ? 'Saving…' : 'Mark paid'}</button>
      <button type="button" data-testid={`booking-cancel-${b.id}`} disabled={!!busy} onClick={() => act('cancel')} className="px-4 py-1.5 rounded-full text-xs font-semibold bg-white border border-red-200 text-red-600 disabled:opacity-60">Cancel & free slot</button>
    </div>
  );
};

export const BookingDetails = ({ b }) => {
  const pkg = b.plan === 'intl' && PACKAGES.find((p) => p.id === b.package);
  const rows = Object.entries(b.history || {}).filter(([, v]) => v);
  if (!pkg && !rows.length && !b.reports?.length) return null;
  return (
    <div data-testid={`booking-details-${b.id}`} className="mt-3 space-y-2">
      {pkg && <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800">{pkg.name} · USD {pkg.usd}</span>}
      {rows.length > 0 && (
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-teal-50/50 rounded-lg p-3">
          {rows.map(([k, v]) => <div key={k}><dt className="inline text-slate-500">{LABELS[k] || k}: </dt><dd className="inline text-slate-700">{v}</dd></div>)}
        </dl>
      )}
      <ReportList reports={b.reports} />
    </div>
  );
};
